// Fabric sample drawer on the product page (snippets/fabric-sample-drawer.liquid).
// A link with data-fabric-sample-open="<handle>" opens the drawer of that fabric instead of going to the fabric's page
// (a click with Ctrl / Cmd / Shift or the middle button still opens the page). "Add a free sample" puts the sample
// straight into the cart, the same way as /collections/fabrics (assets/collection-samples.js): express delivery
// properties, at most 5 samples (products tagged "fabric") in the cart. The cart drawer is refreshed but not opened,
// so the customer stays on the product.
(() => {
  if (window.fabricSampleDrawer) return;
  window.fabricSampleDrawer = true;

  const MAX_SAMPLES = 5;
  const SAMPLE_TAG = "fabric";
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const tagsByHandle = {};

  // variant ids of the samples (products tagged "fabric") in the cart
  async function cartSampleIds() {
    const cart = await (await fetch(`${root}cart.js`)).json();
    const handles = [...new Set(cart.items.map(item => item.handle))].filter(handle => !(handle in tagsByHandle));
    await Promise.all(
      handles.map(async handle => {
        try {
          const product = await (await fetch(`${root}products/${handle}.js`)).json();
          tagsByHandle[handle] = product.tags || [];
        } catch (e) {
          tagsByHandle[handle] = [];
        }
      })
    );
    const ids = new Set();
    cart.items.forEach(item => {
      if ((tagsByHandle[item.handle] || []).includes(SAMPLE_TAG)) ids.add(String(item.variant_id));
    });
    return ids;
  }

  function setState(dialog, state) {
    dialog.dataset.state = state;
    const message = dialog.querySelector("[data-fabric-sample-message]");
    const texts = { added: dialog.dataset.textAdded, limit: dialog.dataset.textLimit, error: dialog.dataset.textError };
    message.textContent = texts[state] || "";
    const add = dialog.querySelector("[data-fabric-sample-add]");
    add.disabled = state === "limit";
  }

  async function refreshState(dialog) {
    try {
      const ids = await cartSampleIds();
      if (ids.has(String(dialog.dataset.variantId))) setState(dialog, "added");
      else if (ids.size >= MAX_SAMPLES) setState(dialog, "limit");
      else setState(dialog, "default");
    } catch (e) {
      setState(dialog, "default");
    }
  }

  async function addSample(dialog, button) {
    button.disabled = true;
    button.toggleAttribute("data-loading", true);
    try {
      const ids = await cartSampleIds();
      if (ids.has(String(dialog.dataset.variantId))) {
        setState(dialog, "added");
        return;
      }
      if (ids.size >= MAX_SAMPLES) {
        setState(dialog, "limit");
        return;
      }
      const item = { id: dialog.dataset.variantId, quantity: 1 };
      if (dialog.dataset.deliveryLabel) {
        item.properties = {
          "_Delivery time": dialog.dataset.deliveryLabel,
          "_Lead Time From": dialog.dataset.deliveryFromTs,
          "_Lead Time Timestamp": dialog.dataset.deliveryToTs
        };
      }
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [item] })
      });
      if (!response.ok) throw new Error("Failed to add the sample to cart");
      setState(dialog, "added");
      // refresh the cart drawer and the header count without opening the cart
      try {
        await document.querySelector("cart-drawer")?.refreshCartDrawer?.();
      } catch (e) {}
    } catch (error) {
      console.error("Error adding the fabric sample to cart:", error);
      setState(dialog, "error");
    } finally {
      button.removeAttribute("data-loading");
      button.disabled = dialog.dataset.state === "limit";
    }
  }

  document.addEventListener("click", event => {
    const link = event.target.closest("[data-fabric-sample-open]");
    if (link) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const drawer = document.querySelector(`drawer-element[data-fabric-sample-drawer="${CSS.escape(link.dataset.fabricSampleOpen)}"]`);
      if (!drawer || typeof drawer.open !== "function") return;
      event.preventDefault();
      const dialog = drawer.querySelector("dialog");
      if (dialog.dataset.state === "error") setState(dialog, "default");
      // closing gives the focus back to what had it before – the link or, in Safari, the gallery track around it
      // (it has tabindex) – and the browser then draws its blue ring on the page; after a tap or a mouse click
      // (event.detail > 0) that focus is dropped, with the keyboard it stays where it was
      if (event.detail > 0) {
        dialog.addEventListener(
          "close",
          () => {
            const drop = () => {
              const focused = document.activeElement;
              if (focused && focused !== document.body && (focused === link || focused.contains(link))) focused.blur();
            };
            drop();
            requestAnimationFrame(drop);
          },
          { once: true }
        );
      }
      drawer.open();
      refreshState(dialog);
      return;
    }

    const add = event.target.closest("[data-fabric-sample-add]");
    if (add) {
      addSample(add.closest("dialog"), add);
      return;
    }

    const goCart = event.target.closest("[data-fabric-sample-go-cart]");
    if (goCart) {
      goCart.closest("drawer-element")?.close();
      window.dispatchEvent(new CustomEvent("cart::open"));
    }
  });
})();
